// Hand-rolled SVG "points race" chart across graded matches.
//
// Design choices:
//   - SVG instead of a chart lib to keep the bundle lean and the look on-brand.
//   - Spotlight is CONTROLLED by the parent (the Stats page owns one legend
//     that drives every visual on the page). The spotlighted line draws last,
//     full opacity, thicker.
//   - Two view modes: cumulative "Total" and "Behind #1" (gap to the leader at
//     each point in time — reads like a chase, leader pinned to the top line).
//   - Round boundaries (R32→R16→QF…) are drawn as dashed verticals with a
//     round label, so the x-axis tells the tournament story, not "match 7".
//   - Tap/drag on the chart scrubs a cursor: shows which fixture that column
//     is and the spotlighted player's value there. Works with touch.

import { useRef, useState } from "react";
import type { Match, PlayerLite, State } from "../state";

type Props = {
  history: State["history"];
  players: PlayerLite[];
  matches: Match[];
  spotlighted: string | null;
  onSpotlight?: (id: string | null) => void;
};

const W = 360;
const H = 210;
const PAD_L = 30;
const PAD_R = 48;
const PAD_T = 20;
const PAD_B = 28;
const INNER_W = W - PAD_L - PAD_R;
const INNER_H = H - PAD_T - PAD_B;

type Mode = "total" | "gap";

export function LeaderboardChart({ history, players, matches, spotlighted }: Props) {
  const [mode, setMode] = useState<Mode>("total");
  const [cursor, setCursor] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  if (history.length === 0) {
    return (
      <div className="lb-chart lb-chart--empty">
        <div className="section-label">POINTS RACE</div>
        <p>The chart appears once the first match is graded.</p>
      </div>
    );
  }

  const matchById = new Map(matches.map((m) => [m.id, m]));
  const latest = history[history.length - 1];

  // Leader total at each history index (for gap mode).
  const leaderAt = history.map((h) => Math.max(0, ...Object.values(h.totals)));

  // Per-player value series in the current mode.
  const valueAt = (h: State["history"][number], i: number, pid: string, prev: number) => {
    const total = h.totals[pid] ?? prev;
    return mode === "total" ? total : leaderAt[i] - total;
  };

  const maxTotal = Math.max(1, ...history.flatMap((h) => Object.values(h.totals)));
  const maxGap = Math.max(
    1,
    ...history.flatMap((h, i) =>
      Object.values(h.totals).map((t) => leaderAt[i] - t),
    ),
  );

  const xFor = (i: number) =>
    history.length === 1
      ? PAD_L + INNER_W * 0.5
      : PAD_L + (INNER_W * i) / (history.length - 1);
  // Total: higher is better (0 at bottom). Gap: 0 (with leader) pinned to the
  // top, growing downward — the chase view.
  const yFor = (v: number) =>
    mode === "total"
      ? PAD_T + INNER_H - (INNER_H * v) / maxTotal
      : PAD_T + (INNER_H * v) / maxGap;

  const series = players.map((p) => {
    let prevTotal = 0;
    const points = history.map((h, i) => {
      const total = h.totals[p.id] ?? prevTotal;
      prevTotal = total;
      const v = valueAt(h, i, p.id, total);
      return { x: xFor(i), y: yFor(v), value: v, total };
    });
    return { player: p, points, rank: latest.ranks[p.id] ?? null };
  });

  // Round boundaries: index where the round changes, plus the label span.
  const rounds: Array<{ from: number; to: number; round: string }> = [];
  history.forEach((h, i) => {
    const round = matchById.get(h.match_id)?.round ?? "?";
    const last = rounds[rounds.length - 1];
    if (last && last.round === round) last.to = i;
    else rounds.push({ from: i, to: i, round });
  });

  // Right-edge rank badges, staggered when endpoints collide.
  const labelYs = new Map<string, number>();
  const rankLabels = [...series]
    .filter((s) => s.rank != null)
    .map((s) => ({ player: s.player, rank: s.rank!, y: s.points[s.points.length - 1].y }))
    .sort((a, b) => a.y - b.y);
  let previousY = PAD_T - 11;
  for (const label of rankLabels) {
    const y = Math.min(PAD_T + INNER_H, Math.max(PAD_T, Math.max(label.y, previousY + 11)));
    labelYs.set(label.player.id, y);
    previousY = y;
  }
  const overflow = previousY - (PAD_T + INNER_H);
  if (overflow > 0) {
    for (const [pid, y] of labelYs) labelYs.set(pid, Math.max(PAD_T, y - overflow));
  }

  const sortedForDraw = [...series].sort((a, b) => {
    const aSpot = spotlighted === a.player.id ? 1 : 0;
    const bSpot = spotlighted === b.player.id ? 1 : 0;
    return aSpot - bSpot;
  });

  // Map a pointer event to the nearest history index.
  const scrub = (clientX: number) => {
    const el = svgRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const xView = ((clientX - rect.left) / rect.width) * W;
    const step = history.length === 1 ? INNER_W : INNER_W / (history.length - 1);
    const idx = Math.round((xView - PAD_L) / step);
    setCursor(Math.max(0, Math.min(history.length - 1, idx)));
  };

  const cursorMatch = cursor != null ? matchById.get(history[cursor].match_id) : null;
  const spotSeries = spotlighted ? series.find((s) => s.player.id === spotlighted) : null;

  return (
    <div className="lb-chart">
      <div className="lb-chart__head">
        <div className="section-label">POINTS RACE</div>
        <div className="lb-chart__toggle" role="tablist" aria-label="Chart mode">
          <button
            type="button"
            className={mode === "total" ? "is-on" : ""}
            onClick={() => setMode("total")}
            role="tab"
            aria-selected={mode === "total"}
          >
            Total
          </button>
          <button
            type="button"
            className={mode === "gap" ? "is-on" : ""}
            onClick={() => setMode("gap")}
            role="tab"
            aria-selected={mode === "gap"}
          >
            Behind #1
          </button>
        </div>
      </div>

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={mode === "total" ? "Cumulative points by match" : "Points behind the leader by match"}
        onPointerDown={(e) => scrub(e.clientX)}
        onPointerMove={(e) => { if (e.buttons > 0) scrub(e.clientX); }}
        style={{ touchAction: "pan-y" }}
      >
        {/* Axes */}
        <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={PAD_T + INNER_H} stroke="rgba(42,26,10,0.18)" strokeWidth={1} />
        <line x1={PAD_L} x2={PAD_L + INNER_W} y1={PAD_T + INNER_H} y2={PAD_T + INNER_H} stroke="rgba(42,26,10,0.18)" strokeWidth={1} />

        {/* Y labels */}
        {mode === "total" ? (
          <>
            <text x={PAD_L - 6} y={PAD_T + 6} fontSize="9" textAnchor="end" fill="#78716c">{maxTotal}</text>
            <text x={PAD_L - 6} y={PAD_T + INNER_H + 2} fontSize="9" textAnchor="end" fill="#78716c">0</text>
          </>
        ) : (
          <>
            <text x={PAD_L - 6} y={PAD_T + 6} fontSize="9" textAnchor="end" fill="#78716c">0</text>
            <text x={PAD_L - 6} y={PAD_T + INNER_H + 2} fontSize="9" textAnchor="end" fill="#78716c">−{maxGap}</text>
          </>
        )}

        {/* Round boundaries + labels */}
        {rounds.map((r, ri) => {
          const startX = xFor(r.from);
          const endX = xFor(r.to);
          const midX = (startX + endX) / 2;
          return (
            <g key={`round-${r.round}-${r.from}`}>
              {ri > 0 && (
                <line
                  x1={(xFor(r.from - 1) + startX) / 2}
                  x2={(xFor(r.from - 1) + startX) / 2}
                  y1={PAD_T}
                  y2={PAD_T + INNER_H}
                  stroke="rgba(42,26,10,0.22)"
                  strokeWidth={1}
                  strokeDasharray="3 3"
                />
              )}
              <text x={midX} y={PAD_T - 8} fontSize="8.5" textAnchor="middle" fontWeight="800" fill="#a8a29e" letterSpacing="0.06em">
                {r.round}
              </text>
            </g>
          );
        })}

        {/* Cursor guide */}
        {cursor != null && (
          <line x1={xFor(cursor)} x2={xFor(cursor)} y1={PAD_T} y2={PAD_T + INNER_H} stroke="#c2410c" strokeWidth={1} strokeDasharray="2 3" opacity={0.8} />
        )}

        {/* Lines */}
        {sortedForDraw.map(({ player, points }) => {
          const isSpot = spotlighted === player.id;
          const dimmed = spotlighted && !isSpot;
          const opacity = dimmed ? 0.2 : isSpot ? 1 : 0.6;
          return (
            <polyline
              key={player.id}
              points={points.map((p) => `${p.x},${p.y}`).join(" ")}
              stroke={player.color}
              strokeWidth={isSpot ? 2.5 : 1.4}
              fill="none"
              opacity={opacity}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <title>{`${player.name}: rank #${latest.ranks[player.id] ?? "—"}, ${points[points.length - 1].total} points`}</title>
            </polyline>
          );
        })}

        {/* Single-point fallback dots */}
        {history.length === 1 && sortedForDraw.map(({ player, points }) => (
          <circle
            key={`dot-${player.id}`}
            cx={points[0].x}
            cy={points[0].y}
            r={spotlighted === player.id ? 3.5 : 2.5}
            fill={player.color}
            opacity={spotlighted && spotlighted !== player.id ? 0.2 : 0.8}
          />
        ))}

        {/* Cursor markers on each line at the scrubbed match */}
        {cursor != null && sortedForDraw.map(({ player, points }) => {
          const isSpot = spotlighted === player.id;
          if (spotlighted && !isSpot) return null;
          const pt = points[cursor];
          return <circle key={`cur-${player.id}`} cx={pt.x} cy={pt.y} r={isSpot ? 3.5 : 2} fill={player.color} stroke="#2a1a0a" strokeWidth={isSpot ? 1 : 0} />;
        })}

        {/* Right-edge rank badges */}
        {series.map(({ player, rank }) => {
          if (rank == null) return null;
          const y = labelYs.get(player.id);
          if (y == null) return null;
          const isSpot = spotlighted === player.id;
          const dimmed = spotlighted && !isSpot;
          const pillW = rank >= 10 ? 22 : 18;
          return (
            <g key={`badge-${player.id}`} opacity={dimmed ? 0.35 : 1}>
              <rect x={PAD_L + INNER_W + 5} y={y - 6} width={pillW} height={12} rx={6} fill="#fff8ec" stroke={player.color} strokeWidth={isSpot ? 1.5 : 1} />
              <text x={PAD_L + INNER_W + 5 + pillW / 2} y={y + 3.2} fontSize="8" textAnchor="middle" fontWeight="800" fill="#2a1a0a">#{rank}</text>
            </g>
          );
        })}
      </svg>

      <div className="lb-chart__cursorline" aria-live="polite">
        {cursor != null && cursorMatch ? (
          <>
            <strong>{cursorMatch.round}</strong> · {cursorMatch.home} {cursorMatch.actual ? `${cursorMatch.actual.home}-${cursorMatch.actual.away}` : "vs"} {cursorMatch.away}
            {spotSeries && (
              <> · {spotSeries.player.name}: {mode === "total"
                ? `${spotSeries.points[cursor].total} pts`
                : spotSeries.points[cursor].value === 0 ? "level with #1" : `${spotSeries.points[cursor].value} behind`}</>
            )}
          </>
        ) : (
          <span className="lb-chart__hint">Touch the chart to replay the race match by match</span>
        )}
      </div>
    </div>
  );
}
